'use client'

import { AnimatePresence } from 'motion/react'
import { div as MotionDiv } from 'motion/react-m'
import { usePathname, useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Suspense, useCallback, useEffect, useState } from 'react'
import { setUserLocale } from '@/actions/locale'
import { Globe, X } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { routing } from '@/i18n/routing'
import { LANGUAGE_NATIVE_NAMES } from '@/lib/constants/regional'
import { toast } from '@/lib/toast'

// Messages in each language (shown in browser's language)
const messages: Record<
  string,
  { title: string; description: string; dismiss: string; switch: string }
> = {
  en: {
    title: 'Change language?',
    description: 'Your browser is set to {language}. Would you like to switch?',
    dismiss: 'Keep current',
    switch: 'Switch to {language}',
  },
  zh: {
    title: '更改语言？',
    description: '您的浏览器设置为{language}。您想切换吗？',
    dismiss: '保持当前',
    switch: '切换到{language}',
  },
  hi: {
    title: 'भाषा बदलें?',
    description: 'आपका ब्राउज़र {language} पर सेट है। क्या आप बदलना चाहेंगे?',
    dismiss: 'वर्तमान रखें',
    switch: '{language} में बदलें',
  },
  es: {
    title: '¿Cambiar idioma?',
    description: 'Tu navegador está configurado en {language}. ¿Te gustaría cambiar?',
    dismiss: 'Mantener actual',
    switch: 'Cambiar a {language}',
  },
  fr: {
    title: 'Changer de langue ?',
    description: 'Votre navigateur est configuré en {language}. Voulez-vous changer ?',
    dismiss: 'Garder actuel',
    switch: 'Passer à {language}',
  },
  ar: {
    title: 'تغيير اللغة؟',
    description: 'متصفحك مضبوط على {language}. هل تريد التبديل؟',
    dismiss: 'الإبقاء على الحالي',
    switch: 'التبديل إلى {language}',
  },
  bn: {
    title: 'ভাষা পরিবর্তন করবেন?',
    description: 'আপনার ব্রাউজার {language} এ সেট করা আছে। আপনি কি পরিবর্তন করতে চান?',
    dismiss: 'বর্তমান রাখুন',
    switch: '{language} এ পরিবর্তন করুন',
  },
  pt: {
    title: 'Mudar idioma?',
    description: 'Seu navegador está configurado para {language}. Gostaria de mudar?',
    dismiss: 'Manter atual',
    switch: 'Mudar para {language}',
  },
  ru: {
    title: 'Сменить язык?',
    description: 'Ваш браузер настроен на {language}. Хотите переключиться?',
    dismiss: 'Оставить текущий',
    switch: 'Переключить на {language}',
  },
  ja: {
    title: '言語を変更しますか？',
    description: 'ブラウザは{language}に設定されています。変更しますか？',
    dismiss: '現在のまま',
    switch: '{language}に変更',
  },
  it: {
    title: 'Cambiare lingua?',
    description: 'Il tuo browser è impostato su {language}. Vuoi cambiare?',
    dismiss: 'Mantieni attuale',
    switch: 'Passa a {language}',
  },
  he: {
    title: 'לשנות שפה?',
    description: 'הדפדפן שלך מוגדר ל{language}. האם תרצה לשנות?',
    dismiss: 'שמור נוכחי',
    switch: 'עבור ל{language}',
  },
  de: {
    title: 'Sprache ändern?',
    description: 'Ihr Browser ist auf {language} eingestellt. Möchten Sie wechseln?',
    dismiss: 'Beibehalten',
    switch: 'Zu {language} wechseln',
  },
  el: {
    title: 'Αλλαγή γλώσσας;',
    description: 'Ο περιηγητής σας είναι ρυθμισμένος στα {language}. Θέλετε να αλλάξετε;',
    dismiss: 'Διατήρηση τρέχουσας',
    switch: 'Αλλαγή σε {language}',
  },
  pl: {
    title: 'Zmienić język?',
    description: 'Twoja przeglądarka jest ustawiona na język {language}. Chcesz go zmienić?',
    dismiss: 'Pozostań przy obecnym',
    switch: 'Przełącz na {language}',
  },
}

const langNames = LANGUAGE_NATIVE_NAMES
const supportedLangs = routing.locales as readonly string[]

function LanguageMismatchPopupContent() {
  const locale = useLocale()
  const tSettings = useTranslations('Settings')
  const pathname = usePathname()
  const router = useRouter()
  const [isVisible, setIsVisible] = useState(false)
  const [detectedLang, setDetectedLang] = useState<string | null>(null)
  const isPublicWidget = pathname.startsWith('/widget/')

  useEffect(() => {
    if (isPublicWidget) {
      setIsVisible(false)
      setDetectedLang(null)
      return
    }

    const checkLanguage = () => {
      // Check browser language
      const [browserLang = ''] = navigator.language.split('-') // 'en-US' -> 'en'

      // If browser lang is supported AND different from current locale
      if (supportedLangs.includes(browserLang) && browserLang !== locale) {
        let dismissed = false
        try {
          dismissed = Boolean(sessionStorage.getItem('lang-popup-dismissed'))
        } catch {
          // The suggestion can still work when browser storage is unavailable.
        }
        if (!dismissed) {
          setDetectedLang(browserLang)
          setIsVisible(true)
        }
      }
    }

    checkLanguage()
  }, [isPublicWidget, locale])

  const handleSwitch = useCallback(async () => {
    if (detectedLang) {
      try {
        await setUserLocale(detectedLang)
        router.refresh()
        setIsVisible(false)
      } catch (error) {
        console.error('Failed to change display language:', error)
        toast.error(tSettings('displayLanguageError'), { error })
      }
    }
  }, [detectedLang, router, tSettings])

  const handleDismiss = useCallback(() => {
    setIsVisible(false)
    try {
      sessionStorage.setItem('lang-popup-dismissed', 'true')
    } catch {
      // Dismiss for this mounted page even when the preference cannot be saved.
    }
  }, [])

  if (isPublicWidget || !isVisible || !detectedLang) {
    return null
  }

  // Get messages in the browser's language
  const msg = messages[detectedLang] || messages.en
  const langName = langNames[detectedLang] || detectedLang

  return (
    <AnimatePresence initial={false}>
      <MotionDiv
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="fixed end-6 bottom-6 z-50 shadow-float"
        data-testid="language-mismatch-popup"
        exit={{ opacity: 0, y: 20, scale: 0.9 }}
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
      >
        <Card className="w-87.5 border-primary/20 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-primary" />
                <CardTitle className="text-body-sm">{msg.title}</CardTitle>
              </div>
              <Button
                className="relative h-6 w-6 after:absolute after:inset-[-8px]"
                onClick={handleDismiss}
                size="icon"
                variant="ghost"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <CardDescription className="text-body-2xs">
              {msg.description.replace('{language}', langName)}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-end gap-2 pt-0 pb-3">
            <Button className="h-8 text-body-2xs" onClick={handleDismiss} size="sm" variant="ghost">
              {msg.dismiss}
            </Button>
            <Button className="h-8 text-body-2xs" onClick={handleSwitch} size="sm">
              {msg.switch.replace('{language}', langName)}
            </Button>
          </CardFooter>
        </Card>
      </MotionDiv>
    </AnimatePresence>
  )
}

export function LanguageMismatchPopup() {
  return (
    <Suspense fallback={null}>
      <LanguageMismatchPopupContent />
    </Suspense>
  )
}
